import * as React from 'react';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  ArrowLeft,
  CheckCircle2,
  Fingerprint,
  KeyRound,
  LifeBuoy,
  Loader2,
  Lock,
  ShieldCheck,
} from 'lucide-react';

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { cn } from '@/lib/utils/cn';

import { kratosApi } from '@/lib/api/kratos';

import { MethodChoiceCard } from '../components/MethodChoiceCard';
import {
  PasswordRequirementsChecklist,
  type PasswordRequirement,
} from '../components/PasswordRequirementsChecklist';
import { PasswordStrengthMeter } from '../components/PasswordStrengthMeter';
import { PasswordInputWithToggle } from '../components/PasswordInputWithToggle';
import { TotpSetupForm } from '../components/TotpSetupForm';
import { BackupCodesPanel } from '../components/BackupCodesPanel';

type PasswordChecks = {
  length: boolean;
  uppercase: boolean;
  lowercase: boolean;
  number: boolean;
  special: boolean;
};

/** Lightweight in-component password evaluator (no external dep). */
function evaluatePassword(pwd: string): { checks: PasswordChecks; score: number } {
  const checks: PasswordChecks = {
    length: pwd.length >= 8,
    uppercase: /[A-Z]/.test(pwd),
    lowercase: /[a-z]/.test(pwd),
    number: /\d/.test(pwd),
    special: /[^A-Za-z0-9]/.test(pwd),
  };
  // score is number of checks met, clamped to 4 (length counts as a baseline,
  // the other 4 each contribute one segment).
  const metCount = Object.values(checks).filter(Boolean).length;
  // 0 if empty; otherwise scale 1..4 based on how many requirements pass.
  let score = 0;
  if (pwd.length > 0) {
    if (metCount >= 5) score = 4;
    else if (metCount === 4) score = 3;
    else if (metCount === 3) score = 2;
    else if (metCount >= 1) score = 1;
  }
  return { checks, score };
}

const STRENGTH_LABEL_KEYS = [
  'auth.settingsPasswordStrengthTooWeak',
  'auth.settingsPasswordStrengthWeak',
  'auth.settingsPasswordStrengthFair',
  'auth.settingsPasswordStrengthStrong',
  'auth.settingsPasswordStrengthExcellent',
] as const;

export function SettingsPage() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const flowId = searchParams.get('flow');

  // ───────────────── UI state ─────────────────
  const [methodSelector, setMethodSelector] = React.useState(true);
  const [registerWebAuthn, setRegisterWebAuthn] = React.useState(false);
  const [registerPassword, setRegisterPassword] = React.useState(false);
  const [registerTotp, setRegisterTotp] = React.useState(false);
  const [registerBackupCodes, setRegisterBackupCodes] = React.useState(false);
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [success, setSuccess] = React.useState(false);
  const [submitError, setSubmitError] = React.useState<string | null>(null);

  // ───────────────── Form data ─────────────────
  const [identifier, setIdentifier] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [confirmPassword, setConfirmPassword] = React.useState('');
  const [securityKeyName, setSecurityKeyName] = React.useState('');
  const [csrfToken, setCsrfToken] = React.useState('');
  const [webAuthnLink, setWebAuthnLink] = React.useState('');
  const [passkeyCreateData, setPasskeyCreateData] = React.useState('');
  const [method, setMethod] = React.useState('');

  // ───────────────── Bootstrap: kick off a settings flow if missing ─────────────────
  // When the user lands on /auth/settings without `?flow=<id>`, ask Kratos to
  // create one. Kratos issues a 303 redirect back to `ui_url?flow=<id>`, so the
  // browser ends up on the same page WITH the flow id and the `useQuery` below
  // can hydrate. If the session is missing or AAL is too low, Kratos handles
  // the redirect to login / step-up itself — we just point the window at it.
  React.useEffect(() => {
    if (flowId) return;
    const kratosUrl = import.meta.env.VITE_IDENTITY_PUBLIC_URL;
    if (!kratosUrl) return;
    // Intentionally omit `return_to` — Kratos already redirects back to
    // the `ui_url` configured in selfservice.flows.settings, which is
    // this same /auth/settings page for each stage. Passing
    // window.location.origin as return_to would require that exact URL
    // to appear in Kratos's `allowed_return_urls` allowlist, and it
    // currently only carries the prod dashboard origin —
    // dev-dash.wyasolutions.com / sdx-dash.wyasolutions.com would 400
    // with `self_service_flow_return_to_forbidden`.
    window.location.href = `${kratosUrl}/self-service/settings/browser`;
  }, [flowId]);

  // ───────────────── Fetch flow ─────────────────
  const { data: flow, isLoading, error } = useQuery({
    queryKey: ['settingsFlow', flowId],
    queryFn: async () => {
      if (!flowId) throw new Error('No flow ID provided');
      const response = await kratosApi.getSettingsFlow({ id: flowId });
      return response.data;
    },
    enabled: !!flowId,
    retry: 1,
    staleTime: 0,
  });

  // ───────────────── Load Kratos WebAuthn script from flow ─────────────────
  React.useEffect(() => {
    if (!flow) return;
    const webauthnNode = flow.ui.nodes.find(
      (node: any) =>
        node.type === 'script' &&
        (node.group === 'webauthn' || node.group === 'passkey')
    );
    if (webauthnNode && !document.getElementById('webauthn_script')) {
      const script = document.createElement('script');
      script.id = 'webauthn_script';
      const attrs = webauthnNode.attributes as any;
      script.src = attrs.src as string;
      script.async = attrs.async as boolean;
      if (attrs.crossorigin) script.crossOrigin = attrs.crossorigin as string;
      if (attrs.integrity) script.integrity = attrs.integrity as string;
      if (attrs.referrerpolicy) script.referrerPolicy = attrs.referrerpolicy as string;
      if (attrs.nonce) script.nonce = attrs.nonce as string;
      document.head.appendChild(script);
    }
  }, [flow]);

  // ───────────────── Extract values from flow ─────────────────
  React.useEffect(() => {
    if (!flow) return;

    if ((flow as any).state === 'success') {
      // Kratos already saved — go to dashboard.
      navigate('/dashboard');
      return;
    }
    if ([403, 404, 410].includes((flow as any).status)) {
      navigate('/auth/login');
      return;
    }

    const nodes = flow.ui.nodes;
    const identifierNode = nodes.find((n: any) => n.attributes.name === 'traits.email');
    const csrfNode = nodes.find((n: any) => n.attributes.name === 'csrf_token');
    const passkeyDataNode = nodes.find(
      (n: any) => n.attributes.name === 'passkey_create_data'
    );
    const webAuthnNode = nodes.find(
      (n: any) => n.attributes.name === 'passkey_register_trigger'
    );

    setIdentifier(
      (identifierNode?.attributes as any)?.value
        ? String((identifierNode?.attributes as any).value)
        : ''
    );
    setCsrfToken(
      (csrfNode?.attributes as any)?.value
        ? String((csrfNode?.attributes as any).value)
        : ''
    );
    setPasskeyCreateData(
      (passkeyDataNode?.attributes as any)?.value
        ? String((passkeyDataNode?.attributes as any).value)
        : ''
    );
    setWebAuthnLink(
      (webAuthnNode?.attributes as any)?.onclick
        ? String((webAuthnNode?.attributes as any).onclick)
        : ''
    );

    // If Kratos came back with errors on the password node, surface them here.
    const passwordNode = nodes.find(
      (n: any) => n.attributes?.name === 'password' && n.group === 'password'
    );
    const passwordErr = passwordNode?.messages?.find((m: any) => m.type === 'error')?.text;
    if (passwordErr) {
      setSubmitError(passwordErr);
      // Make sure the user can act on it: keep them in the password form.
      setMethodSelector(false);
      setRegisterPassword(true);
      setMethod('password');
    }
  }, [flow, navigate]);

  // ───────────────── Password evaluation (live) ─────────────────
  const { checks, score } = React.useMemo(() => evaluatePassword(password), [password]);
  const allRequirementsMet = React.useMemo(
    () => Object.values(checks).every(Boolean),
    [checks]
  );
  const passwordsMatch = password.length > 0 && password === confirmPassword;
  const passwordsExplicitlyMismatched =
    password.length > 0 && confirmPassword.length > 0 && password !== confirmPassword;

  const passwordRequirements: PasswordRequirement[] = [
    { key: 'length', label: t('auth.settingsPasswordReqLength'), met: checks.length },
    { key: 'uppercase', label: t('auth.settingsPasswordReqUppercase'), met: checks.uppercase },
    { key: 'lowercase', label: t('auth.settingsPasswordReqLowercase'), met: checks.lowercase },
    { key: 'number', label: t('auth.settingsPasswordReqNumber'), met: checks.number },
    { key: 'special', label: t('auth.settingsPasswordReqSpecial'), met: checks.special },
  ];

  // ───────────────── Handlers ─────────────────
  const passwordlessSelected = () => {
    setSubmitError(null);
    setMethodSelector(false);
    setRegisterWebAuthn(true);
    setMethod('passkey');
  };

  const passwordSelected = () => {
    setSubmitError(null);
    setMethodSelector(false);
    setRegisterPassword(true);
    setMethod('password');
  };

  /**
   * Privileged-session step-up.
   *
   * Kratos hides the TOTP enrolment nodes (`totp_code`, `totp_qr`,
   * `totp_secret_key`) and the backup codes mutation nodes
   * (`lookup_secret_regenerate`, `lookup_secret_confirm`) from a settings
   * flow when the current session does not satisfy
   * `privileged_session_max_age` (typically after the user logged in
   * minutes ago but not "right now"). The user can't actually mutate
   * those factors without re-authenticating.
   *
   * We detect the missing-node case and bounce the user through a fresh
   * login flow with `aal=aal2&refresh=true`. After they reauth, Kratos
   * redirects back here per the `return_to` and the new settings flow
   * exposes every node. The kratos `allowed_return_urls` config already
   * carries the dashboard origin (we widened it for this exact reason
   * during the Security tab rollout).
   */
  const requestPrivilegedStepUp = () => {
    const kratosUrl = import.meta.env.VITE_IDENTITY_PUBLIC_URL;
    if (!kratosUrl) return;
    const returnTo = `${window.location.origin}/auth/settings`;
    const target =
      `${kratosUrl}/self-service/login/browser` +
      `?aal=aal2&refresh=true&return_to=${encodeURIComponent(returnTo)}`;
    window.location.href = target;
  };

  /** True when the flow ALREADY exposes a node matching `name`. */
  const flowHasNode = (name: string): boolean => {
    if (!flow) return false;
    return flow.ui.nodes.some(
      (n: any) => n.attributes?.name === name,
    );
  };

  const totpSelected = () => {
    setSubmitError(null);
    // If Kratos hasn't exposed the enrolment nodes, the user needs a
    // privileged session — kick off step-up before showing the form.
    // We check `totp_code` because that's the input every TOTP setup
    // round trip carries; `totp_qr` only appears on first enrolment.
    if (!flowHasNode('totp_code')) {
      requestPrivilegedStepUp();
      return;
    }
    setMethodSelector(false);
    setRegisterTotp(true);
    setMethod('totp');
  };

  const backupCodesSelected = () => {
    setSubmitError(null);
    // Same rationale as TOTP — the `regenerate` button is the action
    // node Kratos hides without a privileged session.
    if (!flowHasNode('lookup_secret_regenerate')) {
      requestPrivilegedStepUp();
      return;
    }
    setMethodSelector(false);
    setRegisterBackupCodes(true);
    setMethod('lookup_secret');
  };

  const backToMethodSelector = () => {
    setSubmitError(null);
    setMethodSelector(true);
    setRegisterWebAuthn(false);
    setRegisterPassword(false);
    setRegisterTotp(false);
    setRegisterBackupCodes(false);
    setMethod('');
    setPassword('');
    setConfirmPassword('');
    setSecurityKeyName('');
  };

  /**
   * Submit via fetch — takes full control of the request so we can:
   *  - Guarantee the `password` field is sent (vs. native form submit, which
   *    was dropping it intermittently).
   *  - Parse the JSON response in React instead of letting the browser
   *    navigate cross-origin and render raw JSON.
   *  - Handle errors with clean UX.
   */
  const submitPassword = async () => {
    if (!flow) return;
    setIsSubmitting(true);
    setSubmitError(null);

    const body = new URLSearchParams();
    body.set('csrf_token', csrfToken);
    body.set('method', 'password');
    body.set('traits.email', identifier);
    body.set('password', password);

    try {
      const response = await fetch(flow.ui.action, {
        method: 'POST',
        body,
        credentials: 'include',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
          'Accept': 'application/json',
        },
      });

      if (response.status === 200) {
        setSuccess(true);
        setTimeout(() => navigate('/dashboard'), 1500);
        return;
      }

      if (response.status === 400) {
        const updatedFlow = await response.json();
        queryClient.setQueryData(['settingsFlow', flowId], updatedFlow);
        const passwordErr = updatedFlow?.ui?.nodes
          ?.find((n: any) => n.attributes?.name === 'password')
          ?.messages?.find((m: any) => m.type === 'error')?.text;
        setSubmitError(passwordErr ?? t('auth.settingsErrorGeneric'));
        setIsSubmitting(false);
        return;
      }

      if (response.status === 403 || response.status === 410) {
        navigate('/auth/recovery?expired=true');
        return;
      }

      setSubmitError(t('auth.settingsErrorGeneric'));
      setIsSubmitting(false);
    } catch (err) {
      setSubmitError(t('auth.settingsErrorNetwork'));
      setIsSubmitting(false);
    }
  };

  /**
   * Submit TOTP enrolment programatically. Mirrors `submitPassword`: the
   * Kratos settings flow expects `method=totp`, `totp_code=<6 digits>`, plus
   * the CSRF token. On 400 we re-render the new flow so the user sees the
   * field-level error from Kratos (typically "wrong code").
   */
  const submitTotp = async (code: string) => {
    if (!flow) return;
    setIsSubmitting(true);
    setSubmitError(null);

    const body = new URLSearchParams();
    body.set('csrf_token', csrfToken);
    body.set('method', 'totp');
    body.set('totp_code', code);
    body.set('traits.email', identifier);

    try {
      const response = await fetch(flow.ui.action, {
        method: 'POST',
        body,
        credentials: 'include',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
          'Accept': 'application/json',
        },
      });

      if (response.status === 200) {
        setSuccess(true);
        setTimeout(() => navigate('/dashboard'), 1500);
        return;
      }

      if (response.status === 400) {
        const updatedFlow = await response.json();
        queryClient.setQueryData(['settingsFlow', flowId], updatedFlow);
        const totpErr = updatedFlow?.ui?.nodes
          ?.find((n: any) => n.attributes?.name === 'totp_code')
          ?.messages?.find((m: any) => m.type === 'error')?.text;
        setSubmitError(totpErr ?? t('auth.mfaTotpErrorWrongCode'));
        setIsSubmitting(false);
        return;
      }

      if (response.status === 403) {
        // Kratos signals two distinct 403s here: `session_refresh_required`
        // (privileged session expired — bounce through aal2&refresh and
        // come back), and any other 403 which means the session is gone.
        try {
          const body = await response.clone().json();
          const errId = body?.error?.id ?? body?.ui?.messages?.[0]?.id;
          if (
            errId === 'session_refresh_required' ||
            errId === 'session_aal2_required' ||
            errId === 4000010
          ) {
            requestPrivilegedStepUp();
            return;
          }
        } catch {
          // body wasn't JSON — fall through to the generic "expired" path
        }
        navigate('/auth/login?expired=true');
        return;
      }

      if (response.status === 410) {
        navigate('/auth/login?expired=true');
        return;
      }

      setSubmitError(t('auth.settingsErrorGeneric'));
      setIsSubmitting(false);
    } catch (err) {
      setSubmitError(t('auth.settingsErrorNetwork'));
      setIsSubmitting(false);
    }
  };

  /**
   * Submit lookup_secret actions:
   *  - `regenerate` → asks Kratos for a fresh batch of backup codes.
   *  - `confirm`    → user acknowledges they've stored the codes.
   *
   * Both end up at the same settings flow URL with different fields. We
   * re-use the same fetch shape as `submitPassword` / `submitTotp`.
   */
  const submitLookupSecret = async (action: 'regenerate' | 'confirm') => {
    if (!flow) return;
    setIsSubmitting(true);
    setSubmitError(null);

    const body = new URLSearchParams();
    body.set('csrf_token', csrfToken);
    body.set('method', 'lookup_secret');
    body.set('traits.email', identifier);
    if (action === 'regenerate') {
      body.set('lookup_secret_regenerate', 'true');
    } else {
      body.set('lookup_secret_confirm', 'true');
    }

    try {
      const response = await fetch(flow.ui.action, {
        method: 'POST',
        body,
        credentials: 'include',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
          'Accept': 'application/json',
        },
      });

      if (response.status === 200) {
        // Regenerate keeps the user on the same panel showing the new codes;
        // confirm finalises the activation.
        const updatedFlow = await response.json().catch(() => null);
        if (updatedFlow) {
          queryClient.setQueryData(['settingsFlow', flowId], updatedFlow);
        }
        if (action === 'confirm') {
          setSuccess(true);
          setTimeout(() => navigate('/dashboard'), 1500);
        } else {
          setIsSubmitting(false);
        }
        return;
      }

      if (response.status === 400) {
        const updatedFlow = await response.json();
        queryClient.setQueryData(['settingsFlow', flowId], updatedFlow);
        const err = updatedFlow?.ui?.messages?.find((m: any) => m.type === 'error')
          ?.text;
        setSubmitError(err ?? t('auth.settingsErrorGeneric'));
        setIsSubmitting(false);
        return;
      }

      if (response.status === 403) {
        // Same privileged-session bounce as in submitTotp — backup codes
        // mutations require a fresh AAL2 session.
        try {
          const body = await response.clone().json();
          const errId = body?.error?.id ?? body?.ui?.messages?.[0]?.id;
          if (
            errId === 'session_refresh_required' ||
            errId === 'session_aal2_required' ||
            errId === 4000010
          ) {
            requestPrivilegedStepUp();
            return;
          }
        } catch {
          // body wasn't JSON — fall through
        }
        navigate('/auth/login?expired=true');
        return;
      }

      if (response.status === 410) {
        navigate('/auth/login?expired=true');
        return;
      }

      setSubmitError(t('auth.settingsErrorGeneric'));
      setIsSubmitting(false);
    } catch (err) {
      setSubmitError(t('auth.settingsErrorNetwork'));
      setIsSubmitting(false);
    }
  };

  const handleFormSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    if (registerPassword) {
      // Password path → we own the submit entirely.
      e.preventDefault();
      if (!allRequirementsMet || !passwordsMatch) return;
      void submitPassword();
      return;
    }
    // Passkey / WebAuthn path → leave the native submit alone. Kratos's
    // injected script populates the hidden `passkey_settings_register` /
    // `webauthn_register` field via window.oryPasskeySettingsRegistration()
    // before submit, so the browser native POST is what Kratos expects.
    setIsSubmitting(true);
  };

  // ───────────────── Render helpers ─────────────────
  const getTitle = () => {
    if (success) return t('auth.settingsSuccess');
    if (registerPassword) return t('auth.settingsPasswordSetHeading');
    if (registerWebAuthn) return t('auth.settingsPasskeyTitle');
    if (registerTotp) return t('auth.mfaTotpTitle');
    if (registerBackupCodes) return t('auth.mfaBackupCodesTitle');
    return t('auth.settingsTitle');
  };

  const getSubtitle = () => {
    if (success) return t('auth.settingsRedirecting');
    if (registerPassword && identifier)
      return t('auth.settingsPasswordForEmail', { email: identifier });
    if (registerWebAuthn) return t('auth.settingsPasskeyDescription');
    if (registerTotp) return t('auth.mfaTotpSubtitle');
    if (registerBackupCodes) return t('auth.mfaBackupCodesSubtitle');
    return t('auth.settingsSubtitle');
  };

  // ───────────────── Loading / error states ─────────────────
  // No flow id yet → the useEffect above is redirecting to Kratos. Show a
  // loader instead of the error card during the brief window before the
  // browser navigates away.
  if (!flowId || isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="w-full max-w-md">
          <CardContent className="flex items-center justify-center gap-2 py-10">
            <Loader2 className="h-4 w-4 animate-spin" />
            <span className="text-sm text-muted-foreground">{t('common.loading')}</span>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error || !flow) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle>{t('common.error')}</CardTitle>
            <CardDescription>{t('auth.settingsErrorGeneric')}</CardDescription>
          </CardHeader>
          <CardContent>
            <Button
              onClick={() => (window.location.href = '/auth/login')}
              className="w-full"
            >
              {t('auth.backToLogin')}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center space-y-3">
          <div className="flex justify-center">
            <img
              src="/logo_wya.png"
              alt="WYA"
              className="h-10 w-auto"
            />
          </div>
          <div>
            <CardTitle className="text-2xl font-light">{getTitle()}</CardTitle>
            <CardDescription className="text-base mt-1">{getSubtitle()}</CardDescription>
          </div>
        </CardHeader>

        <CardContent className="space-y-6">
          {/* Email verified confirmation banner (only on method selector) */}
          {methodSelector && identifier && (
            <Alert className="border-green-200 bg-green-50 text-green-900 dark:border-green-900/40 dark:bg-green-950/30 dark:text-green-100">
              <CheckCircle2 className="h-4 w-4 text-green-600 dark:text-green-500" />
              <AlertTitle className="text-sm font-medium">
                {t('auth.settingsEmailVerified')}
              </AlertTitle>
              <AlertDescription className="text-xs">{identifier}</AlertDescription>
            </Alert>
          )}

          {/* Submit-level error (shown above any form) */}
          {submitError && !success && (
            <Alert variant="destructive">
              <AlertDescription className="text-sm">{submitError}</AlertDescription>
            </Alert>
          )}

          {/* Success state */}
          {success && (
            <div className="flex flex-col items-center gap-3 py-2">
              <div className="rounded-full bg-green-100 p-3 dark:bg-green-900/40">
                <CheckCircle2 className="h-6 w-6 text-green-600 dark:text-green-500" />
              </div>
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="h-3 w-3 animate-spin" />
                <span>{t('auth.settingsRedirecting')}</span>
              </div>
            </div>
          )}

          {/* Method selector */}
          {!success && methodSelector && (
            <div className="space-y-3">
              <div className="text-sm font-medium text-foreground">
                {t('auth.settingsChooseMethod')}
              </div>
              <MethodChoiceCard
                icon={<Fingerprint className="h-5 w-5" />}
                title={t('auth.settingsPasskeyTitle')}
                description={t('auth.settingsPasskeyDescription')}
                badge={t('auth.settingsPasskeyBadge')}
                onClick={passwordlessSelected}
              />
              <MethodChoiceCard
                icon={<Lock className="h-5 w-5" />}
                title={t('auth.settingsPasswordTitle')}
                description={t('auth.settingsPasswordDescription')}
                onClick={passwordSelected}
              />
              <MethodChoiceCard
                icon={<KeyRound className="h-5 w-5" />}
                title={t('auth.mfaTotpTitle')}
                description={t('auth.mfaTotpDescription')}
                onClick={totpSelected}
              />
              <MethodChoiceCard
                icon={<LifeBuoy className="h-5 w-5" />}
                title={t('auth.mfaBackupCodesTitle')}
                description={t('auth.mfaBackupCodesSubtitle')}
                onClick={backupCodesSelected}
              />
            </div>
          )}

          {/* Back link (visible when inside any sub-form) */}
          {!success && !methodSelector && (
            <button
              type="button"
              onClick={backToMethodSelector}
              className={cn(
                'inline-flex items-center gap-1 text-xs text-muted-foreground',
                'hover:text-foreground transition-colors',
                'focus-visible:outline-none focus-visible:underline'
              )}
            >
              <ArrowLeft className="h-3 w-3" />
              {t('auth.settingsBackToMethod')}
            </button>
          )}

          {/* Single form covers both password and passkey paths.
              For password: handler does e.preventDefault() + fetch.
              For passkey: handler lets browser do native POST after the
              Kratos-injected JS populates the hidden register field. */}
          {!success && (registerPassword || registerWebAuthn) && (
            <form
              id="settings"
              action={flow.ui.action}
              method="post"
              encType="application/x-www-form-urlencoded"
              onSubmit={handleFormSubmit}
              className="space-y-4"
            >
              {/* Hidden fields shared by both methods */}
              <input type="hidden" name="csrf_token" value={csrfToken} readOnly />
              <input type="hidden" name="method" value={method} readOnly />
              <input
                type="hidden"
                name="traits.email"
                value={identifier}
                readOnly
              />

              {/* Passkey-only hidden fields */}
              {registerWebAuthn && (
                <>
                  <input
                    type="hidden"
                    name="passkey_create_data"
                    value={passkeyCreateData ?? ''}
                    readOnly
                  />
                  <input
                    type="hidden"
                    id="passkey_settings_register"
                    name="passkey_settings_register"
                    defaultValue=""
                  />
                </>
              )}

              {/* Password fields */}
              {registerPassword && (
                <div className="space-y-4">
                  <div className="space-y-2">
                    <label
                      htmlFor="password"
                      className="text-sm font-medium text-foreground"
                    >
                      {t('auth.passwordLabel')}
                    </label>
                    <PasswordInputWithToggle
                      id="password"
                      name="password"
                      placeholder={t('auth.settingsPasswordPlaceholder')}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      disabled={isSubmitting}
                      autoComplete="new-password"
                      required
                      showLabel={t('auth.settingsPasswordShow')}
                      hideLabel={t('auth.settingsPasswordHide')}
                      aria-describedby="password-strength password-requirements"
                    />
                    <div id="password-strength">
                      <PasswordStrengthMeter
                        score={score}
                        label={t(STRENGTH_LABEL_KEYS[score] ?? STRENGTH_LABEL_KEYS[0])}
                      />
                    </div>
                  </div>

                  <div id="password-requirements">
                    <PasswordRequirementsChecklist
                      heading={t('auth.settingsPasswordRequirements')}
                      requirements={passwordRequirements}
                    />
                  </div>

                  <div className="space-y-2">
                    <label
                      htmlFor="confirmPassword"
                      className="text-sm font-medium text-foreground"
                    >
                      {t('auth.confirmPasswordLabel')}
                    </label>
                    <PasswordInputWithToggle
                      id="confirmPassword"
                      name="confirm_password"
                      placeholder={t('auth.settingsPasswordConfirmPlaceholder')}
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      disabled={isSubmitting}
                      autoComplete="new-password"
                      required
                      error={passwordsExplicitlyMismatched ? t('auth.settingsPasswordMismatch') : undefined}
                      showLabel={t('auth.settingsPasswordShow')}
                      hideLabel={t('auth.settingsPasswordHide')}
                      trailingSlot={
                        passwordsMatch ? (
                          <CheckCircle2 className="h-4 w-4 text-green-500" />
                        ) : null
                      }
                    />
                    {passwordsExplicitlyMismatched && (
                      <p className="text-xs text-destructive">
                        {t('auth.settingsPasswordMismatch')}
                      </p>
                    )}
                    {passwordsMatch && (
                      <p className="text-xs text-green-600 dark:text-green-500">
                        {t('auth.settingsPasswordMatch')}
                      </p>
                    )}
                  </div>

                  <Button
                    type="submit"
                    className="w-full h-11"
                    disabled={isSubmitting || !allRequirementsMet || !passwordsMatch}
                    aria-disabled={isSubmitting || !allRequirementsMet || !passwordsMatch}
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        {t('auth.settingsSubmitting')}
                      </>
                    ) : (
                      <>
                        <ShieldCheck className="mr-2 h-4 w-4" />
                        {t('auth.settingsPasswordSave')}
                      </>
                    )}
                  </Button>
                </div>
              )}

              {/* WebAuthn / Passkey registration UI */}
              {registerWebAuthn && (
                <div className="space-y-4">
                  <Input
                    type="text"
                    name="webauthn_register_displayname"
                    label={t('common.name')}
                    placeholder={t('common.name')}
                    value={securityKeyName}
                    onChange={(e) => setSecurityKeyName(e.target.value)}
                    disabled={isSubmitting}
                    autoComplete="off"
                    required
                  />
                  {/* Native button with onclick from Kratos flow — triggers
                      the WebAuthn ceremony. After that, Kratos's JS submits
                      the form natively. */}
                  <button
                    type="button"
                    name="passkey_register_trigger"
                    ref={(el) => {
                      if (el && webAuthnLink) {
                        el.setAttribute('onclick', webAuthnLink);
                      }
                    }}
                    disabled={isSubmitting || !securityKeyName}
                    className={cn(
                      'w-full h-11 rounded-md bg-primary text-primary-foreground text-sm font-medium',
                      'hover:bg-primary/90 transition-colors',
                      'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
                      'disabled:opacity-50 disabled:pointer-events-none'
                    )}
                  >
                    {isSubmitting ? (
                      <span className="inline-flex items-center">
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        {t('auth.settingsSubmitting')}
                      </span>
                    ) : (
                      <span className="inline-flex items-center justify-center">
                        <Fingerprint className="mr-2 h-4 w-4" />
                        {t('auth.settingsPasskeyTitle')}
                      </span>
                    )}
                  </button>
                </div>
              )}
            </form>
          )}

          {/* TOTP setup uses its own self-contained form (the component owns
              the submit handler + fetch). It does not share state with the
              password/passkey form above, so we render it as a sibling. */}
          {!success && registerTotp && (
            <TotpSetupForm
              flow={flow}
              csrfToken={csrfToken}
              identifier={identifier}
              isSubmitting={isSubmitting}
              submitError={submitError}
              onSubmit={submitTotp}
            />
          )}

          {/* Backup codes panel — also self-contained. Triggers the parent's
              `submitLookupSecret` for regenerate / confirm actions. */}
          {!success && registerBackupCodes && (
            <BackupCodesPanel
              flow={flow}
              csrfToken={csrfToken}
              identifier={identifier}
              isSubmitting={isSubmitting}
              submitError={submitError}
              onRegenerate={() => submitLookupSecret('regenerate')}
              onConfirm={() => submitLookupSecret('confirm')}
            />
          )}

          {/* Back to login link */}
          {!success && methodSelector && (
            <div className="text-center pt-2">
              <Link
                to="/auth/login"
                className="text-sm text-muted-foreground hover:text-primary transition-colors"
              >
                {t('auth.backToLogin')}
              </Link>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
